import {
  T,
  addonGroups,
  addonsConfirmLabel,
  formatCents,
  toggleUpsell,
  tt,
  upsellChoices,
  upsellChoicesTotalCents,
  upsellGroupIsFull,
  upsellGroupIsRadio,
  upsellKey,
  upsellLimitLabel,
  upsellPriceLabel,
  upsellUnitCents,
  upsellUnits,
  useModalFocus,
  type Addon,
  type UpsellChoice,
  type UpsellGroup,
  type UpsellOffer,
  type UpsellSelection,
} from '@agos/shared'
import { Plus, X } from 'lucide-react'
import { useRef, useState } from 'react'

/**
 * *Chcete si priobjednať niečo navyše?* - asked once, after the product is already in the basket.
 *
 * **Here rather than in `customer-menu` since §9.657 (owner O44)**, because the till asks the same question now:
 * *"can you make also setting in Predajné Kanály - to show these modals for čašníčka?"*. It imports only
 * `@agos/shared`, `lucide-react` and React, which is what made the move a move rather than a rewrite.
 *
 * The order matters and is legacy's: **add first, ask second**. A customer who dismisses this still has
 * what they came for, and one who never sees it is not blocked. It is an offer, not a step.
 *
 * Add-ons are ordinary products - their own id, price and VAT - so choosing one puts it in the basket as
 * itself. There is no add-on line concept anywhere, because the kitchen makes a dressing and the receipt
 * prints a dressing.
 *
 * **§9.712 (owner O63, O69, answers #133, #134, #136) - the groups come first**, each under its own heading with its
 * limit (*vyberte najviac 1*) and its price (*zadarmo* at nought), as radio buttons where the limit for this prompt is
 * one; then the ungrouped add-ons exactly as before, under their menu category. The confirm hands back every choice
 * **with its terms** - the group it was chosen in and the price it was quoted at - because a free dip and the same
 * dip bought outright are two different lines (`upsell.ts`).
 */
export function AddonPrompt({
  productName,
  offer,
  units = 1,
  onConfirm,
  onClose,
  wide = false,
}: {
  productName: string
  offer: UpsellOffer
  /**
   * How many units of the product this is asked for - one at every call site, more only when a second unit was
   * added before the first question arrived (`upsellPromptAfter`). A group's limit is per unit (#133), so it is
   * multiplied by this, and a one-of group stops being radio buttons the moment two units allow two.
   */
  units?: number
  onConfirm: (chosen: UpsellChoice[]) => void
  onClose: () => void
  /**
   * Three add-ons in a row on a wide screen - §9.662, FB-990.
   *
   * **The owner, about the manager**: *"can you make it like... wider? Not much, but kinda wider.. to see like 3
   * products in a row or something like that"*. The customer's is a bottom sheet on a phone, so this is a
   * variant rather than a change: widening that one would put three truncated names across 390 px.
   *
   * **A boolean rather than a column count**, which keeps the component from answering *how many fit* - a
   * question about the screen it is on. Defaulted off, so the customer app is untouched by construction.
   *
   * Three only from `sm:` up: the manager runs on tablets and phones too (§14.9), and below that breakpoint the
   * grid is one column, which is the opposite of what this request is about.
   */
  wide?: boolean
}) {
  const [selection, setSelection] = useState<UpsellSelection>(new Set())
  const count = upsellUnits(units)
  const choices = upsellChoices(offer, selection)
  const total = upsellChoicesTotalCents(choices)
  const press = (groupId: number | null, addonId: number) =>
    setSelection((current) => toggleUpsell(offer, current, groupId, addonId, count))

  /* §9.489, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing, on a customer's own keyboard.
     **The role is on the card, not the wrapper**: the wrapper is the dismiss scrim and says `role="presentation"`
     so it is not itself announced, which is §9.469's rule arrived at from this markup. The close is
     unconditional because the scrim already calls it on a tap with no guard, and this overlay holds no write. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      data-addon-prompt
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={tt('Chcete si priobjednať niečo navyše?')}
      ref={panelRef}
      onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        data-addon-wide={wide ? 'yes' : 'no'}
        className={`w-full ${wide ? 'sm:max-w-2xl' : 'sm:max-w-sm'} bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col`}
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 shrink-0">
          <div className="min-w-0">
            <h2 className="font-bold text-lg leading-tight">
              <T>Chcete si priobjednať niečo navyše?</T>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{productName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-addon-close
            aria-label={tt('Zavrieť')}
            data-dialog-cancel
            className="p-2 -mr-2 rounded-full hover:bg-secondary transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* §9.712 - the groups first, each with its own terms, then §9.70's ungrouped add-ons as they were. A group is
          * a question the franchise wrote (*Dip ku kúsku*, one free), so it leads; the rest is the menu's own
          * suggestions, which were the whole prompt before. */}
        <div className="px-6 pb-2 space-y-3 overflow-y-auto flex-1">
          {offer.groups.map((group) => (
            <UpsellGroupSection
              key={`group:${String(group.id)}`}
              group={group}
              units={count}
              selection={selection}
              wide={wide}
              onPress={press}
            />
          ))}
          {/* §9.70 - grouped, because twenty in one column makes a dressing and a drink look like the
            * same kind of decision. Uncategorised sit last under no heading: they are the ones nobody
            * filed, not a category called *(žiadna)*. */}
          {addonGroups(offer.items).map((group) => (
            <div key={group.name ?? '(none)'} className="space-y-2" data-addon-group={group.name ?? ''}>
              {group.name !== null && (
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {group.name}
                </p>
              )}
              <div
                className={wide ? 'grid grid-cols-1 sm:grid-cols-3 gap-2' : 'space-y-2'}
                data-addon-grid={wide ? 'yes' : 'no'}
              >
                {group.addons.map((addon) => (
                  <AddonRow
                    key={addon.id}
                    addon={addon}
                    priceText={formatCents(addon.priceCents)}
                    picked={selection.has(upsellKey(null, addon.id))}
                    kind="checkbox"
                    disabled={false}
                    wide={wide}
                    onPress={() => press(null, addon.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-border shrink-0 space-y-2">
          {total > 0 && (
            <p className="text-sm font-bold text-right" data-addon-total>
              +{formatCents(total)}
            </p>
          )}
          <button
            type="button"
            data-addon-confirm
            onClick={() => onConfirm(choices)}
            className="w-full min-h-[48px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold flex items-center justify-center gap-2 transition-colors"
          >
            {choices.length > 0 && <Plus className="w-4 h-4 stroke-[3]" />}
            {addonsConfirmLabel(choices.length)}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * One upsell group - §9.712: its name, its limit and its price as the heading, then its items.
 *
 * **The terms are in the heading and on every row**, because a customer reads a row, not a heading: *zadarmo* beside
 * a dip is what tells them the dip is free, and *vyberte najviac 1* above it is what tells them they cannot have two.
 * At the limit the unticked items are disabled rather than hidden, so the choice stays visible and changeable.
 */
function UpsellGroupSection({
  group,
  units,
  selection,
  wide,
  onPress,
}: {
  group: UpsellGroup
  units: number
  selection: UpsellSelection
  wide: boolean
  onPress: (groupId: number | null, addonId: number) => void
}) {
  const radio = upsellGroupIsRadio(group, units)
  const full = upsellGroupIsFull(selection, group, units)
  const limit = upsellLimitLabel(group, units)
  return (
    <div
      className="space-y-2"
      data-upsell-group={group.id}
      data-upsell-kind={radio ? 'radio' : 'checkbox'}
      role={radio ? 'radiogroup' : 'group'}
      aria-label={group.name}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{group.name}</p>
        <p className="text-[11px] font-semibold text-muted-foreground">
          {limit !== null && <span data-upsell-limit={group.id}>{limit}</span>}
          {limit !== null && group.priceCents !== null && ' · '}
          {group.priceCents !== null && (
            <span
              data-upsell-price={group.id}
              className={group.priceCents === 0 ? 'text-green-700 font-bold' : undefined}
            >
              {upsellPriceLabel(group.priceCents)}
            </span>
          )}
        </p>
      </div>
      <div
        className={wide ? 'grid grid-cols-1 sm:grid-cols-3 gap-2' : 'space-y-2'}
        data-addon-grid={wide ? 'yes' : 'no'}
      >
        {group.items.map((addon) => {
          const picked = selection.has(upsellKey(group.id, addon.id))
          return (
            <AddonRow
              key={addon.id}
              addon={addon}
              priceText={upsellPriceLabel(upsellUnitCents(group, addon))}
              picked={picked}
              kind={radio ? 'radio' : 'checkbox'}
              radioName={`upsell-group-${String(group.id)}`}
              // A one-of group is never full in the way that disables: choosing another replaces the choice.
              disabled={!radio && full && !picked}
              groupId={group.id}
              wide={wide}
              onPress={() => onPress(group.id, addon.id)}
            />
          )
        })}
      </div>
    </div>
  )
}

/**
 * One add-on, ticked or not - the §9.70 row, shared by a group and the ungrouped list so the two cannot drift.
 *
 * **A radio button is pressed with `onClick`, not `onChange`**: a ticked radio fires no change when pressed again, and
 * an upsell is an offer - a customer who picked a dip must be able to pick none. `toggleUpsell` decides what a press
 * means, so the two input kinds share one handler.
 */
function AddonRow({
  addon,
  priceText,
  picked,
  kind,
  radioName,
  disabled,
  groupId,
  wide,
  onPress,
}: {
  addon: Addon
  priceText: string
  picked: boolean
  kind: 'checkbox' | 'radio'
  radioName?: string
  disabled: boolean
  groupId?: number
  wide: boolean
  onPress: () => void
}) {
  return (
    <label
      data-addon={addon.id}
      data-upsell-item={groupId === undefined ? undefined : `${String(groupId)}:${String(addon.id)}`}
      /* §9.664, FB-992 - **`items-start` in the grid**, because a two-line name beside a one-line price
         reads as misaligned when both are centred on a box that grows. */
      className={`flex ${wide ? 'items-start' : 'items-center'} gap-3 p-3 rounded-2xl border transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${picked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
    >
      {kind === 'radio' ? (
        <input
          type="radio"
          name={radioName}
          checked={picked}
          onChange={() => undefined}
          onClick={onPress}
          className="w-5 h-5 accent-primary shrink-0"
        />
      ) : (
        <input
          type="checkbox"
          checked={picked}
          disabled={disabled}
          onChange={onPress}
          className="w-5 h-5 rounded accent-primary shrink-0"
        />
      )}
      {addon.imageUrl !== null && (
        // Half of them have one. A row that reserved space for the other half would be a
        // column of grey boxes, so the picture appears only where there is a picture.
        <img
          src={addon.imageUrl}
          alt=""
          loading="lazy"
          data-addon-image={addon.id}
          className="w-10 h-10 rounded-lg object-cover shrink-0"
        />
      )}
      <span className="flex-1 min-w-0">
        {/* §9.664, FB-992 - the owner: *"make sure it will show whole text... now, its Like Se... DIl...
          * Ko... Vin..."*. A third of the wide panel is about 210 px, so a name that was fine on one row
          * of a phone sheet loses most of itself in a grid cell. Truncating is still right in the sheet,
          * where the name has the whole width and one line is the design. */}
        <span
          className={`block text-sm font-semibold ${wide ? 'break-words' : 'truncate'}`}
          data-addon-name={addon.id}
        >
          {addon.name}
        </span>
        {addon.description !== null && (
          <span className={`block text-xs text-muted-foreground ${wide ? 'break-words' : 'truncate'}`}>
            {addon.description}
          </span>
        )}
      </span>
      <span className="text-sm font-bold shrink-0" data-addon-price={addon.id}>
        {priceText}
      </span>
    </label>
  )
}
